import { useLayoutEffect, useRef, useState } from 'react'
import { Alert, Box, Button, Group, Loader, Paper, ScrollArea, Stack, Text } from '@mantine/core'
import { IconArrowDown } from '@tabler/icons-react'

import type { AutorDaMensagem, MensagemDaConversa } from '@/api/whatsapiOrg'
import { mensagemDeErro } from '@/components/admin/whatsappGateways/mensagens'
import { ROTULO_DO_AUTOR, decisaoDoBot, horario } from './textos'

// Técnico à esquerda; quem fala pelo número do gateway à direita, cada um com a sua cor.
const COR_DO_AUTOR: Record<AutorDaMensagem, string> = {
  tecnico: 'gray', bot: 'blue', humano: 'green', auto_reply: 'grape', api: 'orange',
}

const STATUS_DO_ENVIO: Record<string, string> = {
  queued: 'na fila', pending: 'na fila', sent: 'enviada', delivered: 'entregue', read: 'lida',
}

/** Distância do fim (px) em que a conversa ainda conta como "lendo o fim" e acompanha a mensagem nova. */
export const PERTO_DO_FIM_PX = 80

function Balao({ m }: { m: MensagemDaConversa }) {
  const doTecnico = m.autor === 'tecnico'
  // Envio que o gateway não entregou (a resposta do atendente pode voltar `failed`).
  const falhou = !doTecnico && m.status === 'failed'
  const cor = COR_DO_AUTOR[m.autor] ?? 'gray'
  const rotulo = ROTULO_DO_AUTOR[m.autor] ?? m.autor
  const decisao = doTecnico ? decisaoDoBot(m.decisao, m.motivo) : null
  return (
    <Group justify={doTecnico ? 'flex-start' : 'flex-end'} data-testid={`balao-${m.autor}`}>
      <Paper p="xs" radius="md" maw="80%" bg={`var(--mantine-color-${cor}-light)`}
        withBorder={falhou} style={falhou ? { borderColor: 'var(--mantine-color-red-6)' } : undefined}>
        <Text size="xs" fw={600} c={`${cor}.8`}>
          {m.autor_nome && m.autor !== 'tecnico' ? `${rotulo} · ${m.autor_nome}` : rotulo}
        </Text>
        <Text size="sm" style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{m.texto || '[sem texto]'}</Text>
        <Group gap="xs" justify="space-between">
          {decisao ? <Text size="xs" c="dimmed">Bot: {decisao}</Text> : <span />}
          {falhou ? (
            <Text size="xs" c="red.7" fw={600}>{horario(m.em)} · não entregue</Text>
          ) : (
            <Text size="xs" c="dimmed">
              {horario(m.em)}{m.status && !doTecnico ? ` · ${STATUS_DO_ENVIO[m.status] ?? m.status}` : ''}
            </Text>
          )}
        </Group>
      </Paper>
    </Group>
  )
}

/**
 * Coluna da conversa: balões por autor, mais antigos em cima, "Carregar anteriores" e o aviso
 * de histórico incompleto (as mensagens anteriores a 02/11 não têm autor gravado, §5.1).
 *
 * Rolagem (como um chat): ao abrir, desce até a última mensagem. Mensagem nova desce sozinha se
 * quem lê já estava perto do fim; se subiu para ler o histórico, não é puxado: aparece
 * "Nova mensagem". "Carregar anteriores" mantém a posição de leitura. O estado vale por conversa
 * (a tela remonta o componente com `key` ao trocar de conversa).
 */
export function ConversaAberta({
  mensagens, carregando, erro, historicoIncompleto, temAnteriores, carregandoAnteriores, aoCarregarAnteriores,
}: {
  /** Mais novas primeiro, como vêm da rota. */
  mensagens: MensagemDaConversa[]
  carregando: boolean
  erro: unknown
  historicoIncompleto: boolean
  temAnteriores: boolean
  carregandoAnteriores: boolean
  aoCarregarAnteriores: () => void
}) {
  const viewport = useRef<HTMLDivElement>(null)
  const pertoDoFim = useRef(true)
  const anterior = useRef<{ maisNova?: string; maisAntiga?: string; altura: number }>({ altura: 0 })
  const [temNova, setTemNova] = useState(false)

  const maisNova = mensagens[0]?.id
  const maisAntiga = mensagens[mensagens.length - 1]?.id

  const irProFim = () => {
    const v = viewport.current
    if (!v) return
    v.scrollTop = v.scrollHeight
    pertoDoFim.current = true
    setTemNova(false)
  }

  useLayoutEffect(() => {
    const v = viewport.current
    if (!v || carregando || mensagens.length === 0) return
    const antes = anterior.current
    if (antes.maisNova === undefined) {
      v.scrollTop = v.scrollHeight // abriu a conversa
    } else if (maisNova !== antes.maisNova) {
      if (pertoDoFim.current) v.scrollTop = v.scrollHeight
      else setTemNova(true)
    } else if (maisAntiga !== antes.maisAntiga) {
      v.scrollTop += v.scrollHeight - antes.altura // vieram as anteriores, em cima: fica onde estava
    }
    anterior.current = { maisNova, maisAntiga, altura: v.scrollHeight }
  }, [maisNova, maisAntiga, carregando, mensagens.length])

  const aoRolar = () => {
    const v = viewport.current
    if (!v) return
    pertoDoFim.current = v.scrollHeight - v.scrollTop - v.clientHeight <= PERTO_DO_FIM_PX
    if (pertoDoFim.current) setTemNova(false)
  }

  if (carregando) return <Loader size="sm" />
  if (erro) return <Alert color="red" variant="light">{mensagemDeErro(erro)}</Alert>
  return (
    <Stack gap="xs" pos="relative">
      {historicoIncompleto && (
        <Alert color="yellow" variant="light">O histórico anterior a 02/11 pode estar incompleto.</Alert>
      )}
      <ScrollArea.Autosize mah="65vh" type="auto" viewportRef={viewport} onScrollPositionChange={aoRolar}>
        <Stack gap="xs">
          {temAnteriores && (
            <Box ta="center">
              <Button variant="subtle" size="xs" loading={carregandoAnteriores} onClick={aoCarregarAnteriores}>
                Carregar anteriores
              </Button>
            </Box>
          )}
          {mensagens.length === 0 && <Text size="sm" c="dimmed">Nenhuma mensagem nesta conversa.</Text>}
          {[...mensagens].reverse().map((m) => <Balao key={`${m.autor}-${m.id}`} m={m} />)}
        </Stack>
      </ScrollArea.Autosize>
      {temNova && (
        <Box pos="absolute" bottom={8} left={0} right={0} ta="center" style={{ pointerEvents: 'none' }}>
          <Button size="xs" radius="xl" leftSection={<IconArrowDown size={14} />} onClick={irProFim}
            style={{ pointerEvents: 'auto' }}>
            Nova mensagem
          </Button>
        </Box>
      )}
    </Stack>
  )
}
